import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { boronClient } from "../../../api";
import {
  StudentTagFilterInterface,
  StudentTagType,
} from "../../../domains/StudentTag";
import { createError, HTTPErrors } from "../../../errors";
import { paths } from "../../../lib/api/v1";

const URL = "/api/v1/sections/{section_id}/student_follow_summaries";
type Url = typeof URL;

type StudentFollowSummariesQuery = paths[Url]["get"]["parameters"]["query"];
type StudentFollowSummariesResponse =
  paths[Url]["get"]["responses"]["200"]["content"]["application/json"];
export type StudentFollowSummary =
  StudentFollowSummariesResponse["studentFollowSummaries"][0];

// 絞り込みはタグ・スマートタグ・職業タグの 3 種のみ（LIST-05）。
// 連携状態（status）は API が受け付けず黙って無視するため、キーごと持たない型にして
// 「UI 上は絞り込んでいるのに全件が返る」不整合を型レベルで防ぐ
export type StudentFollowSummaryFilterParams = {
  tag_ids: string[];
  smart_tag: string[];
  job: string[];
};

// 解決済みのタグ選択状態（useStudentFilterContext）から絞り込みパラメータを組み立てる。
// buildStudentFilterParams（FiltersHelper）は status を常に含めるため流用しない。
// 選択状態はタグ候補と突き合わせて解決されたタグのみを含むため、URL に残存した
// 削除済みタグ ID はここに現れない（生徒一覧方式の踏襲）
export const buildStudentFollowSummaryFilterParams = (
  tagFilter: StudentTagFilterInterface,
): StudentFollowSummaryFilterParams => {
  const selectTagIdsByType = (type: StudentTagType) =>
    tagFilter.selectedTags
      .filter((tag) => tag.type === type)
      .map((tag) => tag.id);

  return {
    tag_ids: selectTagIdsByType("tag_ids"),
    smart_tag: selectTagIdsByType("smart_tag"),
    job: selectTagIdsByType("job"),
  };
};

type Props = {
  sectionId: string;
  filterParams: StudentFollowSummaryFilterParams;
  // フィルタ選択状態の復元（URL / localStorage）が終わるまで取得を待つ。
  // 復元前に取得すると「絞り込みなし → あり」で同じページを二重取得するため
  enabled: boolean;
};

type PageParam = {
  afterCursor: string | null;
};

export const useFetchStudentFollowSummaries = ({
  sectionId,
  filterParams,
  enabled,
}: Props) => {
  const { data, ...result } = useInfiniteQuery<
    StudentFollowSummariesResponse,
    HTTPErrors
  >({
    // フィルタ条件ごとにキャッシュを分離する（条件間で使い回さない）
    queryKey: [URL, sectionId, filterParams],
    queryFn: ({ pageParam }) =>
      fetchStudentFollowSummaries({
        sectionId,
        filterParams,
        pageParam: pageParam as PageParam,
      }),
    initialPageParam: { afterCursor: null } satisfies PageParam,
    getNextPageParam: (lastPage): PageParam | undefined => {
      if (lastPage.meta.nextCursor !== null) {
        return { afterCursor: lastPage.meta.nextCursor };
      }
      return undefined;
    },
    enabled,
  });

  // 並び順は API の返却順を維持する（LIST-01: フロントでの並べ替えはしない）。
  // 全件コピーになるため、取得成功で data が差し替わったときだけ再計算する
  const summaries = useMemo(
    () => data?.pages.flatMap((page) => page.studentFollowSummaries),
    [data],
  );

  const pages = data?.pages;

  return {
    summaries,
    // 一覧の最終更新日時（サーバー導出・studyplus/boron#7016）。可視範囲内の参照対象サマリー
    // 全体で最新の生成完了日時で、絞り込み条件とカーソルに依存しない。参照対象 0 件は null。
    // 全ページ同じ意味の値が返るため、最後に取得したレスポンスの値（最新）を使う
    lastGeneratedAt: pages?.[pages.length - 1]?.meta.lastGeneratedAt ?? null,
    // 取得済みページ数。次ページ取得トリガーの番兵 <div> の key に使う（WEB-1947 方式。
    // 意図の詳細は useFetchTimelineStudents の同名値を参照）
    loadedPageCount: pages?.length ?? 0,
    ...result,
  };
};

const fetchStudentFollowSummaries = async ({
  sectionId,
  filterParams,
  pageParam,
}: Omit<Props, "enabled"> & {
  pageParam: PageParam;
}): Promise<StudentFollowSummariesResponse> => {
  // 生成型のクエリキーは "tag_ids[]" 形式だが、boronClient の querySerializer
  // （queryString.stringify arrayFormat: "bracket"）がブラケットを付与するため、
  // 実行時はブラケットなしのキーで渡してキャストする（useFetchTimelineStudents と同じ扱い）
  const query = {
    ...filterParams,
    after_cursor: pageParam.afterCursor ?? undefined,
  } as StudentFollowSummariesQuery;

  const { data, response } = await boronClient.GET(URL, {
    params: {
      path: { section_id: sectionId },
      query,
    },
  });

  if (response.ok && data) {
    return data;
  }

  throw await createError(response);
};
